import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import { useEligibilityData } from "../useEligibilityData";
import styles from "../index.module.css";

const labelStyle: React.CSSProperties = {
  fontSize: "0.68rem",
  fontWeight: 600,
  color: "#999",
  letterSpacing: "0.7px",
  textTransform: "uppercase",
  marginBottom: "0.3rem",
  display: "block",marginTop:'-0.1rem'
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.72rem 0.9rem 0.72rem 2.4rem",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  fontSize: "0.88rem",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "Outfit, sans-serif",
  color: "#333",
  background: "#fff",
};

const iconWrap: React.CSSProperties = {
  position: "absolute",
  left: "0.75rem",
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  alignItems: "center",
  pointerEvents: "none",
};

const PHONEPE_INSTITUTE_ID = "a77b11e5-df02-4e78-9dff-49434aa7bb02";

function EligibilityEmandate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromDigio = searchParams.get("from") === "digio";

  const userData = useEligibilityData();

  const mobile = userData?.mobile || "";

  const [accountNumber, setAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [accountType, setAccountType] = useState<"SAVINGS" | "CURRENT">("SAVINGS");
  const [radioSelectedMandate, setRadioSelectedMandate] = useState("net_banking");
  const [authToken, setAuthToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [maskedAccount, setMaskedAccount] = useState("");
  const [checkingStatus, setCheckingStatus] = useState(fromDigio);
  const [instituteId, setInstituteId] = useState("");
  const isPhonePe = instituteId === PHONEPE_INSTITUTE_ID;

  useEffect(() => {
    if (!userData?.userId || !userData?.applicationId) return;
    const eligToken = sessionStorage.getItem("eligibility_auth_token");
    let cancelled = false;

    fetch(`${process.env.REACT_APP_DASHBOARD_URL}/summary/user?id=${userData.userId}&applicationId=${userData.applicationId}`, {
      headers: { Authorization: `Bearer ${eligToken}` },
    })
      .then((res) => res.json())
      .then((res) => {
        if (cancelled) return;
        const rawInstId = res?.data?.instituteId;
        setInstituteId((Array.isArray(rawInstId) ? rawInstId[0]?.id : rawInstId) || "");
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [userData?.userId, userData?.applicationId]);

  useEffect(() => {
    if (!userData?.userId) return;
    const eligToken = sessionStorage.getItem("eligibility_auth_token");
    let cancelled = false;

    const run = async () => {
      try {
        const tokenRes = await fetch(`${process.env.REACT_APP_DASHBOARD_URL}/integrations-login/generate-token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(eligToken ? { Authorization: `Bearer ${eligToken}` } : {}),
          },
          body: JSON.stringify({ mobile, userId: userData.userId, applicationId: userData.applicationId }),
        });
        const tokenData = await tokenRes.json();
        if (cancelled) return;
        const integToken = tokenData.data;
        setAuthToken(integToken);

        const maxAttempts = fromDigio ? 5 : 1;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          if (attempt > 0) await new Promise((r) => setTimeout(r, 2000));
          if (cancelled) return;

          const authRes = await fetch(`${process.env.REACT_APP_DASHBOARD_URL}/integrations-login/auth`, {
            headers: { Authorization: `Bearer ${integToken}`, "Content-Type": "application/x-www-form-urlencoded" },
          });
          const authData = await authRes.json();
          if (cancelled) return;

          const enachStatus = authData.data?.enachStatus;
          if (enachStatus === 6 || enachStatus === 18 || enachStatus === 14 || enachStatus === 11 || enachStatus === 4) {
            navigate("/eligibility/success", { state: { data: userData } });
            return;
          }
        }

        setCheckingStatus(false);
        if (fromDigio) setError("eMandate setup was not completed. Please try again.");
        if (!userData.applicationId || !userData.userId) return;
        const bankRes = await fetch(
          `${process.env.REACT_APP_DASHBOARD_URL}/onemoney/bank-details?applicationId=${userData.applicationId}&userId=${userData.userId}`,
          { headers: { Authorization: `Bearer ${eligToken}` } }
        );
        if (cancelled) return;
        const bankData = await bankRes.json();
        setMaskedAccount(bankData?.data?.accountNumber || "");
      } catch {}
    };

    run();
    return () => { cancelled = true; };
  }, [userData?.userId, userData?.applicationId]);

  if (checkingStatus) {
    return (
      <div className={styles.body}>
        <div className={styles.backdrop}>
          <Navbar />
          <div className={styles.main}>
            <div className={styles.cardWrapper}>
              <div className={styles.container} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "260px", gap: "1rem" }}>
                <svg width="40" height="40" viewBox="0 0 34 34" fill="none" style={{ animation: "spin 1s linear infinite" }}>
                  <circle cx="17" cy="17" r="13" stroke="rgba(211,32,40,0.2)" strokeWidth="3" />
                  <path d="M17 4C24.18 4 30 9.82 30 17" stroke="#d32028" strokeWidth="3" strokeLinecap="round" />
                </svg>
                <p style={{ margin: 0, fontSize: "0.9rem", color: "#555", fontFamily: "Outfit, sans-serif" }}>Verifying your eMandate setup...</p>
              </div>
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              <Footer light />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const unmaskedSuffix = maskedAccount.replace(/^[Xx]+/, "");
  const accountMatches =
    !unmaskedSuffix ||
    (accountNumber.length >= unmaskedSuffix.length &&
      accountNumber.slice(-unmaskedSuffix.length) === unmaskedSuffix);

  const handlePhonePeSubmit = async () => {
    if (!authToken) { setError("Please wait while we load the service."); return; }
    setLoading(true);
    setError("");
    try {
      const result = await fetch(
        `${process.env.REACT_APP_DASHBOARD_URL}/phonepay-upi/register-mandate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            userId: userData?.userId,
            applicationId: userData?.applicationId,
            mobile,
          }),
        }
      );
      const response = await result.json();
      if (response.redirectUrl) {
        window.open(response.redirectUrl, "_self", "noopener,noreferrer");
      } else {
        setError(response.message || "Failed to initiate Phonepe Mandate. Please try again.");
      }
    } catch {
      setError("Failed to initiate Phonepe Mandate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (isPhonePe) { handlePhonePeSubmit(); return; }
    if (!accountNumber.trim()) { setError("Please enter your account number."); return; }
    if (!ifscCode.trim()) { setError("Please enter your IFSC code."); return; }
    if (!authToken) { setError("Please wait while we load the service."); return; }
    setLoading(true);
    setError("");
    try {
      const result = await fetch(
        `${process.env.REACT_APP_DASHBOARD_URL}/digio-emandates/register-emandate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            accountNumber,
            accountType,
            customerMobile: mobile,
            ifsc: ifscCode,
            authType: "aadhaar",
            userId: userData?.userId,
            applicationId: userData?.applicationId,
          }),
        }
      );
      const response = await result.json();
      if (response.mandate_id && response.authentication_url) {
        const redirectUrl = `${process.env.REACT_APP_FRONTEND_URL}/eligibility/emandate?from=digio`;
        window.open(
          `${process.env.REACT_APP_DIGIO_BASEURL}/#/gateway/login/${response.access_token.entity_id}/vI3atY/${mobile}?token_id=${response.access_token.id}&redirect_url=${redirectUrl}`,
          "_self",
          "noopener,noreferrer"
        );
      } else {
        setError(response.message || "Failed to initiate eMandate. Please try again.");
      }
    } catch {
      setError("Failed to initiate eMandate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" stroke="#d32028" strokeWidth="1.7" />
          <path d="M9 12L11 14L15 10" stroke="#d32028" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      label: "RBI",
      desc: "Approved",
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="11" width="18" height="11" rx="2" stroke="#d32028" strokeWidth="1.7" />
          <path d="M7 11V7C7 4.79 9.24 3 12 3C14.76 3 17 4.79 17 7V11" stroke="#d32028" strokeWidth="1.7" strokeLinecap="round" />
          <circle cx="12" cy="16" r="1.5" fill="#d32028" opacity="0.6" />
        </svg>
      ),
      label: "Secure",
      desc: "Bank-grade",
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="6" width="20" height="14" rx="2" stroke="#d32028" strokeWidth="1.7" />
          <path d="M2 10H22" stroke="#d32028" strokeWidth="1.7" />
          <path d="M6 15H8M10 15H12" stroke="#d32028" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      ),
      label: "Auto",
      desc: "Debit EMI",
    },
  ];

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>

              {/* ── Header ── */}
              <div className={styles.cardHeader}>
                <div
                  style={{
                    width: "2.4rem",
                    height: "2.4rem",
                    borderRadius: "50%",
                    background: "#F4E5DD",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 9L12 4L21 9V20C21 20.55 20.55 21 20 21H4C3.45 21 3 20.55 3 20V9Z"
                      fill="#d32028"
                      opacity="0.15"
                      stroke="#d32028"
                      strokeWidth="1.5"
                      strokeLinejoin="round"
                    />
                    <rect x="9" y="13" width="6" height="8" rx="1" stroke="#d32028" strokeWidth="1.4" />
                  </svg>
                </div>
                <p className={styles.cardTitle}>eMandate Setup</p>
              </div>

              {/* ── Description ── */}
              <p
                style={{
                  fontSize: "0.82rem",
                  color: "#888",
                  margin: "0.1rem 0 0.9rem",
                  lineHeight: 1.5,
                }}
              >
                {isPhonePe
                  ? "Set up automatic EMI deductions via UPI Autopay on PhonePe."
                  : "Provide your bank details to set up automatic EMI deductions via eMandate."}
              </p>

              {/* ── Form ── */}
              {!isPhonePe && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>

                {/* Account Number + IFSC in one row */}
                  <div style={{ flex: 1 }}>
                    <span style={labelStyle}>Account Number</span>
                    <div style={{ position: "relative" }}>
                      <span style={iconWrap}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                          <rect x="2" y="6" width="20" height="14" rx="2" stroke="#aaa" strokeWidth="1.6" />
                          <path d="M2 10H22" stroke="#aaa" strokeWidth="1.6" />
                          <path d="M6 15H8M10 15H12" stroke="#aaa" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      </span>
                      <input
                        style={inputStyle}
                        type="text"
                        placeholder="Account number"
                        value={accountNumber}
                        onChange={(e) => { setAccountNumber(e.target.value); setError(""); }}
                      />
                    </div>
                    {maskedAccount && (
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        marginTop: "0.45rem",
                        padding: "0.55rem 0.75rem",
                        background: accountMatches ? "#f0fdf4" : "#fff9f9",
                        borderRadius: "8px",
                        border: `1px solid ${accountMatches ? "#bbf7d0" : "#f9d8d6"}`,
                      }}>
                        {accountMatches ? (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                            <circle cx="12" cy="12" r="9" stroke="#16a34a" strokeWidth="1.8" />
                            <path d="M8 12L11 15L16 9" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                            <circle cx="12" cy="12" r="9" stroke="#d32028" strokeWidth="1.8" />
                            <path d="M12 8V12M12 16h.01" stroke="#d32028" strokeWidth="1.8" strokeLinecap="round" />
                          </svg>
                        )}
                        {accountMatches ? (
                          <span style={{ fontSize: "0.75rem", color: "#16a34a", fontWeight: 600 }}>Account number matches</span>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: "0.1rem" }}>
                            <span style={{ fontSize: "0.69rem", color: "#999" }}>
                              Enter the account number
                            </span>
                            <span style={{ fontSize: "0.75rem", color: "#d32028", fontWeight: 600, fontFamily: "monospace", letterSpacing: "0.05em" }}>
                              ****{unmaskedSuffix}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={labelStyle}>IFSC Code</span>
                    <div style={{ position: "relative" }}>
                      <span style={iconWrap}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                          <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#aaa" strokeWidth="1.6" strokeLinejoin="round" />
                          <path d="M22 8L12 14L2 8" stroke="#aaa" strokeWidth="1.6" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <input
                        style={{ ...inputStyle, textTransform: "uppercase" }}
                        type="text"
                        placeholder="e.g. SBIN0001234"
                        value={ifscCode}
                        onChange={(e) => { setIfscCode(e.target.value.toUpperCase()); setError(""); }}
                      />
                    </div>
                  </div>

                {/* Account Type */}
                <div>
                  <span style={labelStyle}>Account Type</span>
                  <div style={{ display: "flex", gap: "1rem" }}>
                    {(["SAVINGS", "CURRENT"] as const).map((type) => (
                      <label
                        key={type}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          cursor: "pointer",
                          color: accountType === type ? "#d32028" : "#666",
                          fontFamily: "Outfit, sans-serif",
                          fontSize: "0.85rem",
                          fontWeight: accountType === type ? 600 : 400,
                        }}
                      >
                        <input
                          type="radio"
                          name="accountType"
                          checked={accountType === type}
                          onChange={() => setAccountType(type)}
                          style={{
                            cursor: "pointer",
                            accentColor: "#d32028",
                            width: "16px",
                            height: "16px",
                          }}
                        />
                        {type.charAt(0) + type.slice(1).toLowerCase()}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Auth Method */}
                {/* <div>
                  <span style={labelStyle}>Method</span>
                  <div style={{display:"inline-flex", gap: "0.55rem" }}>
                    {([
                      { value: "net_banking", label: "Net Banking" },
                      { value: "debit", label: "Debit Card" },
                      { value: "aadhaar", label: "Aadhaar" },
                    ]).map((opt) => (
                      <label
                        key={opt.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          cursor: "pointer",
                          color: radioSelectedMandate === opt.value ? "#d32028" : "#666",
                          fontFamily: "Outfit, sans-serif",
                          fontSize: "0.78rem",
                          fontWeight: radioSelectedMandate === opt.value ? 600 : 400,
                          whiteSpace: "nowrap"
                        }}
                      >
                        <input
                          type="radio"
                          name="authMethod"
                          checked={radioSelectedMandate === opt.value}
                          onChange={() => setRadioSelectedMandate(opt.value)}
                          style={{
                            cursor: "pointer",
                            accentColor: "#d32028",
                            width: "16px",
                            height: "16px"
                          }}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div> */}
              </div>
              )}

              {/* ── Feature tiles ── */}
              <div style={{ display: "flex", gap: "0.55rem", marginTop: "1rem" }}>
                {features.map((item) => (
                  <div
                    key={item.label}
                    style={{
                      flex: 1,
                      background: "#fff9f9",
                      border: "1px solid #f9d8d6",
                      borderRadius: "10px",
                      padding: "0.55rem 0.4rem",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "0.2rem" }}>
                      {item.icon}
                    </div>
                    <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#222", marginBottom: "0.12rem" }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: "0.67rem", color: "#999" }}>{item.desc}</div>
                  </div>
                ))}
              </div>

              {error && (
                <p style={{ color: "#d32028", fontSize: "0.82rem", marginTop: "0.75rem", marginBottom: 0 }}>
                  {error}
                </p>
              )}
            </div>

            {/* ── Proceed + Skip ── */}
            <div
              style={{
                padding: "0rem 1.25rem 1rem",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "0.6rem",justifyContent:'center'
              }}
            >
              <button
                onClick={handleSubmit}
                disabled={loading || (!isPhonePe && !accountMatches)}
                style={{
                  width: "70%",
                  padding: "0.8rem 1.5rem",
                  background: loading || (!isPhonePe && !accountMatches) ? "#aaa" : "#d32028",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: loading || !accountMatches ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                {loading ? "Please wait..." : (
                  <>
                    Proceed to eMandate
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <button
                  onClick={() => navigate("/eligibility/success", { state: { data: userData } })}
                  style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                >
                  Skip
                </button>
              )}
            </div>
          <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilityEmandate;
